import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type AddZoneParams,
  type CoverageZone,
  type UpdateZoneParams,
} from "../../domain/entities/coverage";
import {
  AddZoneError,
  ListCoverageError,
  UpdateZoneError,
} from "../../domain/errors/errors";
import { type ZonesRepository } from "../../domain/repos/zones-repo";
import {
  type CoverageResponse,
  type CoverageZoneResponse,
} from "../../infrastructure/responses/coverage-responses";

export class ZonesRepo implements ZonesRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listCoverage(countryCode: string): Promise<CoverageZone[]> {
    try {
      const query = new URLSearchParams({ country_code: countryCode });
      const response = await this.apiClient.get<CoverageResponse>(
        `v1/coverage?${query.toString()}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListCoverageError(message, apiError);
    }
  }

  async updateZone(params: UpdateZoneParams): Promise<CoverageZone> {
    try {
      // Only send the fields being changed; the interceptor snake_cases them
      // (dailyCapacity -> daily_capacity).
      const body: Record<string, unknown> = {};
      if (params.active !== undefined) body.active = params.active;
      if (params.alias !== undefined) body.alias = params.alias;
      if (params.dailyCapacity !== undefined) body.dailyCapacity = params.dailyCapacity;
      if (params.latitude !== undefined) body.latitude = params.latitude;
      if (params.longitude !== undefined) body.longitude = params.longitude;
      const response = await this.apiClient.patch<Record<string, unknown>, CoverageZoneResponse>(
        `v1/coverage/zones/${params.locationId}`,
        body,
      );
      const zone = response.data;
      if (!zone) throw new UpdateZoneError("Missing zone in response");
      return zone;
    } catch (error) {
      if (error instanceof UpdateZoneError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new UpdateZoneError(message, apiError);
    }
  }

  async addZone(params: AddZoneParams): Promise<CoverageZone> {
    try {
      const response = await this.apiClient.post<AddZoneParams, CoverageZoneResponse>(
        "v1/coverage/zones",
        params,
      );
      const zone = response.data;
      if (!zone) throw new AddZoneError("Missing zone in response");
      return zone;
    } catch (error) {
      if (error instanceof AddZoneError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new AddZoneError(message, apiError);
    }
  }
}
